import { useState, useMemo } from 'react';
import { useStore } from '../store';
import { Btn, Input, Modal, useT } from './UI';

export default function HistorialSerieRoot() {
  const theme = useT();
  const { data } = useStore();
  const { reparaciones } = data;
  const [q, setQ] = useState('');
  const [serieSel, setSerieSel] = useState(null);
  const [marcados, setMarcados] = useState({});
  const [modalImpresora, setModalImpresora] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);

  const grupos = useMemo(() => {
    if (!q.trim()) return [];
    const ql = q.trim().toLowerCase();
    const matches = reparaciones.filter(r =>
      (r.serieRoot && r.serieRoot.toLowerCase().includes(ql)) || (r.clienteNombre || '').toLowerCase().includes(ql));
    const map = new Map();
    for (const r of matches) {
      const nombreMatch = (r.clienteNombre || '').toLowerCase().includes(ql);
      // Match por nombre: agrupa TODAS las reparaciones del cliente (tengan serie o no).
      // Match solo por serie: agrupa por PC, como antes.
      const key = nombreMatch ? 'cliente::' + (r.clienteNombre || '').toLowerCase() : 'serie::' + r.serieRoot;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()].map(([key, reps]) => {
      const ultima = reps.slice().sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))[0];
      const series = [...new Set(reps.map(r => r.serieRoot).filter(Boolean))];
      return {
        serieRoot: series.length ? series.join(', ') : 'Sin serie', key, reps,
        clienteNombre: ultima.clienteNombre,
        equipo: [ultima.tipoEquipo, ultima.equipo].filter(Boolean).join(' - '),
      };
    }).sort((a, b) => (a.clienteNombre || '').localeCompare(b.clienteNombre || ''));
  }, [q, reparaciones]);

  function abrirPc(g) {
    setSerieSel(g);
    const m = {};
    g.reps.forEach(r => { m[r.id] = true; });
    setMarcados(m);
  }

  const repsOrdenadas = serieSel ? serieSel.reps.slice().sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')) : [];
  const seleccionadas = repsOrdenadas.filter(r => marcados[r.id]);

  function imprimir(impresora) {
    if (!seleccionadas.length) return;
    setImprimiendo(true);
    fetch('/api/historial-serieroot/imprimir', {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        serieRoot: serieSel.serieRoot,
        clienteNombre: serieSel.clienteNombre,
        impresora,
        reparaciones: seleccionadas.map(r => ({
          num: r.num, fecha: r.fecha, notas: r.notas || '',
          lineasEntrega: r.lineasEntrega || [], equipo: r.equipo, tipoEquipo: r.tipoEquipo,
        })),
      }),
    })
      .then(r => r.json())
      .then(d => { if (!d.ok) alert('Error: ' + d.error); else setModalImpresora(false); })
      .catch(e => alert('Error: ' + e.message))
      .finally(() => setImprimiendo(false));
  }

  return (
    <div style={{ background: theme.surface, borderRadius: 12, border: `1px solid ${theme.border}`, padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink, marginBottom: 4 }}>🔎 Historial por Serie Root</div>
      <div style={{ fontSize: 12, color: theme.faint, marginBottom: 16 }}>Busca un PC por Serie Root o nombre de cliente e imprime el histórico de reparaciones</div>

      <Input label="Serie Root o nombre del cliente" value={q} onChange={v => { setQ(v); setSerieSel(null); }} placeholder="ej: R123 o Manuel González" />

      {!serieSel && q.trim() && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {grupos.length === 0 && <div style={{ fontSize: 12, color: theme.faint }}>Sin coincidencias</div>}
          {grupos.map(g => (
            <div key={g.key} onClick={() => abrirPc(g)}
              style={{ cursor: 'pointer', padding: '8px 12px', border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.surface2, fontSize: 13 }}>
              <b>{g.serieRoot}</b> — {g.clienteNombre || 'Particular'} — {g.equipo}{' '}
              <span style={{ color: theme.faint }}>({g.reps.length} reparación{g.reps.length !== 1 ? 'es' : ''})</span>
            </div>
          ))}
        </div>
      )}

      {serieSel && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{serieSel.serieRoot} — {serieSel.clienteNombre}</div>
            <Btn size="sm" variant="ghost" onClick={() => setSerieSel(null)}>← Volver</Btn>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
            {repsOrdenadas.map(r => (
              <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!marcados[r.id]} onChange={e => setMarcados(m => ({ ...m, [r.id]: e.target.checked }))} />
                <span style={{ fontWeight: 600, color: '#1a56db' }}>{r.num}</span>
                <span style={{ color: theme.faint }}>{new Date(r.fecha).toLocaleDateString('es-ES')}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.averia}</span>
              </label>
            ))}
          </div>
          <Btn variant="primary" disabled={!seleccionadas.length} onClick={() => setModalImpresora(true)}>
            🖨️ Imprimir seleccionadas ({seleccionadas.length})
          </Btn>
        </div>
      )}

      {modalImpresora && (
        <Modal title="¿En qué impresora?" onClose={() => setModalImpresora(false)} width={360}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Btn variant="outline" disabled={imprimiendo} onClick={() => imprimir('notas')}>🧾 Impresora de Notas (POS-58)</Btn>
            <Btn variant="outline" disabled={imprimiendo} onClick={() => imprimir('termica')}>🖨️ Térmica de ventas (80mm)</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
